/**
 * Kinyonga — the Mbegu mascot 🦎 (ported from the approved v2 design).
 * A chameleon: independent eyes that track your pointer and wander on
 * their own (we watch everything = "we verify"), colour-change themes per
 * product line, moods, seedling-spiral tail, and a pest-zap tongue.
 *
 * variants: 'full' (interactive body) · 'head' (avatar lockup, crisp ≥28px)
 */
import React, { useEffect, useRef } from 'react';

export type KinTheme = 'veg' | 'tree' | 'fruit' | 'chilli' | 'flower';
export type KinMood = 'happy' | 'curious' | 'sleepy' | 'party';

const THEMES: Record<KinTheme, { kb: string; kb2: string; belly: string; crest: string }> = {
  veg:    { kb: '#79C98F', kb2: '#4FAE6C', belly: '#EDF8EE', crest: '#3E8F58' },
  tree:   { kb: '#4E9E70', kb2: '#337A52', belly: '#DDEFE2', crest: '#1F5B3B' },
  fruit:  { kb: '#A3C36A', kb2: '#7E9C48', belly: '#F2F6DE', crest: '#5C7D33' },
  chilli: { kb: '#E48A69', kb2: '#C25E3E', belly: '#FBE7DC', crest: '#A8432A' },
  flower: { kb: '#C094D3', kb2: '#9E6BAA', belly: '#F5E9F8', crest: '#7E5590' },
};

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;
  const s = document.createElement('style');
  s.textContent = `
    @keyframes kinBreathe{0%,100%{transform:scale(1)}50%{transform:scale(1.014)}}
    @keyframes kinRock{0%,100%{transform:rotate(-1.4deg)}50%{transform:rotate(1.4deg)}}
    @keyframes kinWag{0%,100%{transform:rotate(-3deg)}50%{transform:rotate(4deg)}}
    @keyframes kinTwinkle{0%,100%{opacity:0;transform:scale(.5)}50%{opacity:1;transform:scale(1.1)}}
    .kin-breathe{transform-origin:160px 210px;animation:kinBreathe 3.4s ease-in-out infinite}
    .kin-rock{transform-origin:160px 220px;animation:kinRock 5s ease-in-out infinite}
    .kin-tail{transform-origin:120px 208px;animation:kinWag 4.6s ease-in-out infinite}
    .kin-spark{opacity:0}
    .kin[data-m=party] .kin-spark{animation:kinTwinkle 1.2s ease-in-out infinite}
    .kin-tongue{transform-origin:160px 196px;transform:scale(0);transition:transform .13s ease-in}
    .kin-tongue.zap{transform:scale(1)}
    .kin-lidL,.kin-lidR{transform:scaleY(0);transition:transform .45s ease}
    .kin-lidL{transform-origin:132px 132px}.kin-lidR{transform-origin:188px 132px}
    .kin[data-m=sleepy] .kin-lidL,.kin[data-m=sleepy] .kin-lidR{transform:scaleY(.62)}
    .kin-head{transition:transform .5s ease;transform-origin:160px 150px}
    .kin[data-m=curious] .kin-head{transform:rotate(-5deg)}
    @media(prefers-reduced-motion:reduce){
      .kin-breathe,.kin-rock,.kin-tail,.kin-spark{animation:none!important}
      .kin-tongue,.kin-lidL,.kin-lidR,.kin-head{transition:none!important}
    }`;
  document.head.appendChild(s);
}

const Kinyonga: React.FC<{
  variant?: 'full' | 'head';
  theme?: KinTheme;
  mood?: KinMood;
  size?: number;
  caption?: string;
  className?: string;
}> = ({ variant = 'full', theme = 'veg', mood = 'happy', size = 220, caption, className = '' }) => {
  const t = THEMES[theme];
  const rootRef = useRef<HTMLDivElement>(null);
  const pL = useRef<SVGCircleElement>(null);
  const pR = useRef<SVGCircleElement>(null);
  const gL = useRef<SVGCircleElement>(null);
  const gR = useRef<SVGCircleElement>(null);
  const tongueRef = useRef<SVGPathElement>(null);
  const tipRef = useRef<SVGCircleElement>(null);
  const flyRef = useRef<SVGGElement>(null);
  const moodRef = useRef(mood);
  moodRef.current = mood;

  useEffect(() => { injectStyles(); }, []);

  /* Independent eye tracking + idle scan (the chameleon brand behaviour) */
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !pL.current || !pR.current) return;
    const isHead = variant === 'head';
    const EL = isHead ? { x: 42, y: 58 } : { x: 132, y: 150 };
    const ER = isHead ? { x: 78, y: 58 } : { x: 188, y: 150 };
    const R = isHead ? 4.6 : 7.2;
    const vb = isHead ? 120 : 320;
    let tgtL = { x: 0, y: 0 }, tgtR = { x: 0, y: 0 };
    const curL = { x: 0, y: 0 }, curR = { x: 0, y: 0 };
    let lastMove = 0, scanT = 0, raf = 0;

    const aim = (E: { x: number; y: number }, mx: number, my: number) => {
      const a = Math.atan2(my - E.y, mx - E.x);
      return { x: Math.cos(a) * R, y: Math.sin(a) * R };
    };
    const onMove = (e: PointerEvent) => {
      const el = rootRef.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const mx = (e.clientX - r.left) * (vb / r.width);
      const my = (e.clientY - r.top) * (vb / r.height);
      lastMove = performance.now();
      if (moodRef.current === 'curious') return;
      tgtL = aim(EL, mx, my); tgtR = aim(ER, mx, my);
    };
    document.addEventListener('pointermove', onMove, { passive: true });

    const loop = (now: number) => {
      if (now - lastMove > 2600 && now > scanT && moodRef.current !== 'curious' && moodRef.current !== 'sleepy') {
        scanT = now + 900 + Math.random() * 1400;
        tgtL = { x: (Math.random() * 2 - 1) * R, y: (Math.random() * 2 - 1) * R * 0.8 };
        if (Math.random() < 0.7) tgtR = { x: (Math.random() * 2 - 1) * R, y: (Math.random() * 2 - 1) * R * 0.8 };
      }
      if (moodRef.current === 'curious') { tgtL = { x: -R * 0.6, y: -R * 0.75 }; tgtR = { x: R * 0.6, y: R * 0.55 }; }
      if (moodRef.current === 'sleepy') { tgtL = { x: 0, y: R * 0.4 }; tgtR = { x: 0, y: R * 0.4 }; }
      curL.x += (tgtL.x - curL.x) * 0.22; curL.y += (tgtL.y - curL.y) * 0.22;
      curR.x += (tgtR.x - curR.x) * 0.09; curR.y += (tgtR.y - curR.y) * 0.09;
      pL.current?.setAttribute('cx', String(EL.x + curL.x)); pL.current?.setAttribute('cy', String(EL.y + curL.y));
      pR.current?.setAttribute('cx', String(ER.x + curR.x)); pR.current?.setAttribute('cy', String(ER.y + curR.y));
      gL.current?.setAttribute('cx', String(EL.x + curL.x + 2.3)); gL.current?.setAttribute('cy', String(EL.y + curL.y - 2.3));
      gR.current?.setAttribute('cx', String(ER.x + curR.x + 2.3)); gR.current?.setAttribute('cy', String(ER.y + curR.y - 2.3));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); document.removeEventListener('pointermove', onMove); };
  }, [variant]);

  /* Tongue zap on click (full variant) */
  const zap = () => {
    if (variant !== 'full') return;
    const t1 = tongueRef.current, t2 = tipRef.current, f = flyRef.current;
    if (!t1 || !t2) return;
    t1.classList.add('zap'); t2.classList.add('zap');
    setTimeout(() => { if (f) f.style.opacity = '0'; }, 130);
    setTimeout(() => { t1.classList.remove('zap'); t2.classList.remove('zap'); }, 280);
    setTimeout(() => { if (f) f.style.opacity = '1'; }, 1800);
  };

  const smile = mood === 'sleepy' ? 'M144 192 q16 8 32 0'
    : mood === 'curious' ? 'M146 194 q14 6 26 -2'
    : 'M140 192 q20 15 40 0';

  if (variant === 'head') {
    return (
      <div ref={rootRef} className={`kin inline-block ${className}`} data-m={mood}
        role="img" aria-label={caption ?? 'Kinyonga, the Mbegu chameleon'}>
        <svg width={size} height={size} viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="60" fill="#E9F2E6" />
          <circle fill={t.crest} cx="42" cy="26" r="7" /><circle fill={t.crest} cx="60" cy="20" r="8.5" /><circle fill={t.crest} cx="78" cy="26" r="7" />
          <path fill={t.kb} d="M60 112 C26 112 14 84 20 56 C25 34 41 22 60 22 C79 22 95 34 100 56 C106 84 94 112 60 112Z" />
          <circle fill={t.kb2} cx="42" cy="58" r="17" /><circle fill={t.kb2} cx="78" cy="58" r="17" />
          <circle cx="42" cy="58" r="11.5" fill="#F7FBF5" /><circle cx="78" cy="58" r="11.5" fill="#F7FBF5" />
          <circle ref={pL} cx="42" cy="58" r="5" fill="#1B241C" /><circle ref={pR} cx="78" cy="58" r="5" fill="#1B241C" />
          <circle ref={gL} cx="44" cy="56" r="1.6" fill="#fff" /><circle ref={gR} cx="80" cy="56" r="1.6" fill="#fff" />
          <circle fill="#F2B8A2" cx="30" cy="82" r="5.5" opacity=".75" /><circle fill="#F2B8A2" cx="90" cy="82" r="5.5" opacity=".75" />
          <path d="M46 86 q14 11 28 0" fill="none" stroke="#1B241C" strokeWidth="3" strokeLinecap="round" />
        </svg>
        {caption && <p className="text-sm text-muted-foreground mt-2 text-center">{caption}</p>}
      </div>
    );
  }

  return (
    <div ref={rootRef} className={`kin inline-flex flex-col items-center ${className}`} data-m={mood}
      role="img" aria-label={caption ?? 'Kinyonga, the Mbegu chameleon'}
      onClick={zap} style={{ cursor: 'pointer' }} title="Tap to zap the pest">
      <svg width={size} height={size * 0.875} viewBox="0 0 320 280">
        <ellipse cx="160" cy="248" rx="112" ry="18" fill="#2F9E5E" opacity=".28" />
        <path d="M48 248 C50 236 70 228 160 228 C250 228 270 236 272 248 Z" fill="#2F9E5E" opacity=".18" />
        <g className="kin-rock">
          <g className="kin-tail">
            <path d="M118 224 C76 230 48 214 48 188 C48 168 66 158 82 166 C94 172 94 188 82 190 C75 191 71 184 76 180"
              fill="none" stroke={t.kb} strokeWidth="17" strokeLinecap="round" />
          </g>
          <g className="kin-breathe">
            <path fill={t.kb} d="M160 240 C108 240 86 200 94 156 C101 118 128 94 160 94 C192 94 219 118 226 156 C234 200 212 240 160 240 Z" />
            <path fill={t.belly} d="M160 236 C124 236 108 204 114 168 C119 140 138 122 160 122 C182 122 201 140 206 168 C212 204 196 236 160 236 Z" />
            <g fill={t.kb2} opacity=".14">
              <circle cx="118" cy="140" r="4" /><circle cx="206" cy="146" r="4" /><circle cx="112" cy="176" r="3.4" />
              <circle cx="212" cy="182" r="3.4" /><circle cx="130" cy="118" r="3" /><circle cx="192" cy="116" r="3" />
            </g>
            <path d="M100 178 C88 184 82 194 86 204" stroke={t.kb2} strokeWidth="15" fill="none" strokeLinecap="round" />
            <path d="M220 178 C232 184 238 194 234 204" stroke={t.kb2} strokeWidth="15" fill="none" strokeLinecap="round" />
            <ellipse fill={t.kb2} cx="136" cy="238" rx="14" ry="9" /><ellipse fill={t.kb2} cx="184" cy="238" rx="14" ry="9" />
            <g className="kin-head">
              <circle fill={mood === 'party' ? '#E8B84B' : t.crest} cx="134" cy="98" r="11" />
              <circle fill={mood === 'party' ? '#E8B84B' : t.crest} cx="160" cy="88" r="13" />
              <circle fill={mood === 'party' ? '#E8B84B' : t.crest} cx="186" cy="98" r="11" />
              <g className="kin-spark" style={{ animationDelay: '.2s' }}><path d="M128 74 l2.5 5 l5 2.5 l-5 2.5 l-2.5 5 l-2.5 -5 l-5 -2.5 l5 -2.5 Z" fill="#E8B84B" /></g>
              <g className="kin-spark" style={{ animationDelay: '.7s' }}><path d="M196 70 l2 4 l4 2 l-4 2 l-2 4 l-2 -4 l-4 -2 l4 -2 Z" fill="#E8B84B" /></g>
              <g className="kin-spark"><path d="M162 58 l2 4 l4 2 l-4 2 l-2 4 l-2 -4 l-4 -2 l4 -2 Z" fill="#E8B84B" /></g>
              <circle fill={t.kb2} cx="132" cy="150" r="25" /><circle fill={t.kb2} cx="188" cy="150" r="25" />
              <circle cx="132" cy="150" r="17.5" fill="#F7FBF5" /><circle cx="188" cy="150" r="17.5" fill="#F7FBF5" />
              <circle ref={pL} cx="132" cy="150" r="7.4" fill="#1B241C" /><circle ref={pR} cx="188" cy="150" r="7.4" fill="#1B241C" />
              <circle ref={gL} cx="134.4" cy="147.6" r="2.2" fill="#fff" /><circle ref={gR} cx="190.4" cy="147.6" r="2.2" fill="#fff" />
              <rect className="kin-lidL" fill={t.kb2} x="114" y="132" width="36" height="36" rx="18" />
              <rect className="kin-lidR" fill={t.kb2} x="170" y="132" width="36" height="36" rx="18" />
              <circle fill="#F2B8A2" cx="112" cy="184" r="8" opacity=".75" /><circle fill="#F2B8A2" cx="208" cy="184" r="8" opacity=".75" />
              <path d={smile} fill="none" stroke="#1B241C" strokeWidth="3.2" strokeLinecap="round" />
            </g>
          </g>
        </g>
        <path ref={tongueRef} className="kin-tongue" d="M160 196 C132 168 100 118 74 76" stroke="#E76F51" strokeWidth="7" fill="none" strokeLinecap="round" />
        <circle ref={tipRef} className="kin-tongue" cx="74" cy="76" r="6" fill="#E76F51" />
        <g ref={flyRef}><circle cx="72" cy="72" r="5" fill="#1B241C" /><ellipse cx="66" cy="68" rx="4.4" ry="2.6" fill="#9db4c0" opacity=".85" /><ellipse cx="78" cy="68" rx="4.4" ry="2.6" fill="#9db4c0" opacity=".85" /></g>
        <path d="M160 34 c5.5 6.5 5.5 13 0 17 c-5.5 -4 -5.5 -10.5 0 -17Z" fill="#7FD3E8" />
      </svg>
      {caption && <p className="text-sm text-muted-foreground mt-1 text-center max-w-[260px]">{caption}</p>}
    </div>
  );
};

export default Kinyonga;
